import { Box, Container, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import ActiveCampaignEmbed from '../components/ActiveCampaignEmbed';
import SectionHeader from '../components/atomic/SectionHeader';
import Footer from '../components/Footer';
import FormErrorBoundary from '../components/FormErrorBoundary';
import Header from '../components/Header';

/**
 * StoryPageLayout - For business outcome stories with side-by-side form
 *
 * This layout prioritizes STORY CONTENT as the primary feature, with a sticky
 * form sidebar for lead capture. Use this for non-gated educational content
 * where the story demonstrates value and the form captures interested leads.
 *
 * STRUCTURE:
 * 1. Header (navigation)
 * 2. SectionHeader (dark theme with title/subtitle)
 * 3. Main Content Area (two-column layout)
 *    a. Story Content (left column, 2/3 width, white background)
 *       - Accepts any content via children prop
 *       - Typically: Customer, Business Driver, Assessment Approach, Business Outcome sections
 *    b. Form Sidebar (right column, 1/3 width, sticky positioning)
 *       - Call-to-action heading
 *       - ActiveCampaign form in white card container
 *       - Sticks to viewport as user scrolls through story
 *       - Stacks below story on mobile/tablet
 * 4. Footer
 *
 * WHEN TO USE:
 * - Business outcome showcases
 * - Customer success stories
 * - Case studies with lead capture
 * - Educational content with assessment/download form
 *
 * WHEN NOT TO USE:
 * - Gated content where form is primary (use FormPageLayout)
 * - Simple content pages without forms (use ContentPageLayout)
 * - Legal pages (use LegalPageLayout)
 *
 * EXAMPLE USAGE:
 * ```jsx
 * <StoryPageLayout
 *   title="Healthcare & Life Sciences Modernization Assessment"
 *   subtitle="Advancing Metabolic Healthcare with AWS Cloud & AI Services"
 *   formHeading="Request Your Modernization Assessment"
 *   formFilePath="/business-outcome-hcls.txt"
 * >
 *   <CustomerSection />
 *   <BusinessDriverSection />
 *   <AssessmentApproachSection />
 *   <BusinessOutcomeSection />
 * </StoryPageLayout>
 * ```
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Page title displayed in SectionHeader (dark theme)
 * @param {string} props.subtitle - Page subtitle displayed in SectionHeader
 * @param {React.ReactNode} props.children - Story content sections (Customer, Business Driver, etc.)
 * @param {string} props.formHeading - Heading displayed above form in sidebar
 * @param {string} props.formFilePath - Path to ActiveCampaign form HTML file (e.g., '/form.txt')
 * @param {string} [props.heroImage] - Optional path to hero image (displays above two-column layout)
 * @param {string} [props.heroImageAlt] - Alt text for hero image
 * @param {React.ReactNode} [props.heroContent] - Optional custom hero (e.g. an illustration); used instead of heroImage
 * @param {string} [props.introText] - Optional introduction text before story sections
 * @param {React.ReactNode} [props.sidebarContent] - Optional content above form (e.g., message, signature)
 * @param {string} [props.errorMessage] - Custom error message for form loading failures
 * @param {string} [props.loadingMessage] - Custom loading message while form is fetching
 * @returns {import('react').ReactElement} The story page layout
 */
function StoryPageLayout({
  title,
  subtitle,
  children,
  formHeading,
  formFilePath,
  heroImage,
  heroImageAlt = 'Hero image',
  heroContent,
  introText,
  sidebarContent,
  errorMessage = 'Unable to load form',
  loadingMessage = 'Loading form...',
}) {
  const [formHTML, setFormHTML] = useState('');

  useEffect(() => {
    // Fetch the form HTML from the text file in the public folder
    fetch(formFilePath)
      .then((response) => response.text())
      .then((html) => setFormHTML(html))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error loading form:', error);
      });
  }, [formFilePath]);

  return (
    <>
      <Header
        scrollToCollective={() => {}}
        scrollToServices={() => {}}
        scrollToContact={() => {}}
      />

      {/* Breathing room between the nav and the title band */}
      <Box
        sx={{
          backgroundColor: 'var(--color-background)',
          pt: 4,
        }}
      >
        <SectionHeader title={title} subtitle={subtitle} dark={true} />
      </Box>

      {/* Main Content Area - Two Column Layout */}
      <Box
        sx={{
          backgroundColor: 'var(--color-background)',
          pt: { xs: 3, md: 4 },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
          {/* Custom hero - spans full width above columns (optional) */}
          {heroContent && <Box sx={{ mb: 6 }}>{heroContent}</Box>}

          {/* Hero Image - spans full width above columns (optional) */}
          {!heroContent && heroImage && (
            <Box
              component="img"
              src={heroImage}
              alt={heroImageAlt}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: { xs: '250px', md: '350px' },
                objectFit: 'cover',
                borderRadius: '8px',
                mb: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            />
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 4, md: 6 },
              alignItems: 'flex-start',
            }}
          >
            {/* Story Content - Left Column (2/3 width) */}
            <Box
              sx={{
                flex: { xs: '1 1 100%', md: '1 1 66.666%' },
                minWidth: 0,
                pr: { xs: 0, md: 4 },
                borderRight: {
                  xs: 'none',
                  md: '3px solid var(--color-primary)',
                },
              }}
            >
              {/* Optional Introduction Text */}
              {introText && (
                <Box sx={{ mb: 6 }}>
                  <Typography
                    sx={{
                      fontSize: '1.0625rem',
                      lineHeight: 1.75,
                      color: 'var(--color-text)',
                      fontWeight: 400,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {introText}
                  </Typography>
                </Box>
              )}

              {children}
            </Box>

            {/* Form Sidebar - Right Column (1/3 width, sticky) */}
            <Box
              sx={{
                flex: { xs: '1 1 100%', md: '1 1 33.333%' },
                minWidth: 0,
                position: { xs: 'static', md: 'sticky' },
                top: 20,
                alignSelf: 'flex-start',
                pl: { xs: 0, md: 4 },
              }}
            >
              {/* Optional Sidebar Content (message, signature, etc.) */}
              {sidebarContent && <Box sx={{ mb: 4 }}>{sidebarContent}</Box>}

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  color: 'var(--color-heading)',
                  fontSize: { xs: '1.35rem', md: '1.5rem' },
                  letterSpacing: '-0.02em',
                }}
              >
                {formHeading}
              </Typography>

              {/* ActiveCampaign Form Embed */}
              <FormErrorBoundary fallbackMessage={errorMessage}>
                {formHTML && (
                  <ActiveCampaignEmbed
                    htmlContent={formHTML}
                    hideTitle={Boolean(formHeading)}
                  />
                )}

                {/* Loading state */}
                {!formHTML && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: 'var(--color-text-muted)' }}
                    >
                      {loadingMessage}
                    </Typography>
                  </Box>
                )}
              </FormErrorBoundary>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}

export default StoryPageLayout;
